import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'wouter';
import { AnimatePresence, motion, useDragControls, type PanInfo } from 'motion/react';
import { analytics } from '@mindstudio-ai/interface';
import { useIdeaWorkspace, type IdeaWorkspace } from './store';
import {
  PHASES,
  SURFACE_LABEL,
  nextStepFor,
  sameStepIn,
  scopeKeyForPath,
  type ScopeKey,
  type WorkspaceSurface,
} from './surfaces';
import './ideaScope.css';

/* ---------- shared bits ---------- */

/** One 14px status mark: not started, in progress, done, or needs an earlier step. */
export function StatusMark({ s }: { s: Pick<WorkspaceSurface, 'status' | 'gated'> }) {
  const cls = s.status === 'done' ? 'done' : s.status === 'doing' ? 'doing' : s.gated ? 'gated' : 'todo';
  return <span className={`isc-st isc-st--${cls}`} aria-hidden="true" />;
}

function statusWord(s: WorkspaceSurface) {
  return s.status === 'done' ? 'done' : s.status === 'doing' ? 'in progress' : s.gated ? 'needs an earlier step' : 'not started';
}

function track(from: ScopeKey, to: ScopeKey | 'all', source: string) {
  analytics.track('idea_scope_nav', { from, to, source });
}

const monogram = (title: string) => (title.trim()[0] ?? 'I').toUpperCase();

/** The grouped step list, shared by the rail and the phone sheet. */
function StepList({
  ws,
  here,
  next,
  onPick,
  variant,
}: {
  ws: IdeaWorkspace;
  here: ScopeKey;
  next: WorkspaceSurface | null;
  onPick: (to: ScopeKey) => void;
  variant: 'rail' | 'sheet';
}) {
  const byKey = new Map(ws.surfaces.map((s) => [s.key, s]));
  const pct = Math.round((ws.doneCount / Math.max(1, ws.total)) * 100);
  return (
    <div className={`isc-list isc-list--${variant}`}>
      <Link
        href={`/ideas/${ws.idea.id}`}
        className={`isc-row${here === 'overview' ? ' is-on' : ''}`}
        aria-current={here === 'overview' ? 'page' : undefined}
        onClick={() => onPick('overview')}
      >
        <span className="isc-pie" style={{ ['--p' as string]: pct }} aria-hidden="true" />
        <span className="isc-row__label">Command Center</span>
        {variant === 'sheet' && here === 'overview' && <em>You're here</em>}
      </Link>
      {/* This idea's quick notes and memo highlights. */}
      <Link
        href={`/ideas/${ws.idea.id}/notes`}
        className={`isc-row${here === 'notes' ? ' is-on' : ''}`}
        aria-current={here === 'notes' ? 'page' : undefined}
        onClick={() => onPick('notes')}
      >
        <span className="isc-notes-ic" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M2.5 1.5h6.2L11.5 4.3v8.2h-9z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
            <path d="M4.5 6.5h5M4.5 9h3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        </span>
        <span className="isc-row__label">Notes</span>
        {variant === 'sheet' && here === 'notes' ? <em>You're here</em> : ws.noteCount ? <em>{ws.noteCount} open</em> : null}
      </Link>
      {/* This idea's dated Invention Log. */}
      <Link
        href={`/ideas/${ws.idea.id}/log`}
        className={`isc-row${here === 'log' ? ' is-on' : ''}`}
        aria-current={here === 'log' ? 'page' : undefined}
        onClick={() => onPick('log')}
      >
        <span className="isc-notes-ic" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <rect x="2" y="1.5" width="10" height="11" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
            <path d="M4.5 4.5h5M4.5 7h5M4.5 9.5h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        </span>
        <span className="isc-row__label">Invention log</span>
        {variant === 'sheet' && here === 'log' && <em>You're here</em>}
      </Link>
      {/* The Ready-to-Share Packet: this idea's documents gathered into one dated PDF. */}
      <Link
        href={`/ideas/${ws.idea.id}/packet`}
        className={`isc-row${here === 'packet' ? ' is-on' : ''}`}
        aria-current={here === 'packet' ? 'page' : undefined}
        onClick={() => onPick('packet')}
      >
        <span className="isc-notes-ic" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 4l-8 4l8 4l8 -4l-8 -4" />
            <path d="M4 12l8 4l8 -4" />
            <path d="M4 16l8 4l8 -4" />
          </svg>
        </span>
        <span className="isc-row__label">Ready to share</span>
        {variant === 'sheet' && here === 'packet' && <em>You're here</em>}
      </Link>
      {PHASES.map((ph) => {
        const rows = ph.keys.map((k) => byKey.get(k)).filter((s): s is WorkspaceSurface => !!s);
        const done = rows.filter((r) => r.status === 'done').length;
        return (
          <div key={ph.id} role="group" aria-label={ph.label}>
            <div className="isc-cap">
              <span>{ph.label}</span>
              <span>
                {done}/{rows.length}
              </span>
            </div>
            {/* The idea's Business Planning hub (discovery, canvas and tracking together): reachable from the rail. */}
            {ph.id === 'business' && (
              <Link
                href={`/ideas/${ws.idea.id}/business-planning`}
                className={`isc-row${here === 'businessPlanning' ? ' is-on' : ''}`}
                aria-current={here === 'businessPlanning' ? 'page' : undefined}
                onClick={() => onPick('businessPlanning')}
              >
                <span className="isc-notes-ic" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                    <rect x="1.5" y="1.5" width="4.5" height="4.5" rx="1" stroke="currentColor" strokeWidth="1.2" />
                    <rect x="8" y="1.5" width="4.5" height="4.5" rx="1" stroke="currentColor" strokeWidth="1.2" />
                    <rect x="1.5" y="8" width="11" height="4.5" rx="1" stroke="currentColor" strokeWidth="1.2" />
                  </svg>
                </span>
                <span className="isc-row__label">Planning overview</span>
                {variant === 'sheet' && here === 'businessPlanning' && <em>You're here</em>}
              </Link>
            )}
            {rows.map((s) => {
              const on = here === s.key;
              const isNext = next?.key === s.key;
              return (
                <Link
                  key={s.key}
                  href={s.href}
                  className={`isc-row${on ? ' is-on' : ''}${s.gated ? ' is-gated' : ''}`}
                  aria-current={on ? 'page' : undefined}
                  aria-label={`${SURFACE_LABEL[s.key]}, ${statusWord(s)}${isNext ? ', next step' : ''}`}
                  onClick={() => onPick(s.key)}
                >
                  <StatusMark s={s} />
                  <span className="isc-row__label">{SURFACE_LABEL[s.key]}</span>
                  {isNext && !on ? (
                    <span className="isc-next">Next</span>
                  ) : variant === 'sheet' && on ? (
                    <em>You're here</em>
                  ) : s.meta ? (
                    <em>{s.meta}</em>
                  ) : null}
                </Link>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/** Rows at their final heights while the workspace loads (nothing pops in). */
function StepListSkeleton() {
  return (
    <div className="isc-list isc-list--rail" aria-hidden="true">
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} className="isc-row isc-row--sk">
          <span className="isc-st isc-st--todo" />
          <span className="isc-sk" style={{ width: `${55 + ((i * 17) % 35)}%` }} />
        </div>
      ))}
    </div>
  );
}

/** Other ideas, same step. */
function Switcher({ ws, location, onDone }: { ws: IdeaWorkspace; location: string; onDone: () => void }) {
  return (
    <div className="isc-switch" role="menu">
      <div className="isc-switch__cap">Switch idea</div>
      {ws.recentIdeas.length === 0 && <div className="isc-switch__none">No other ideas yet.</div>}
      {ws.recentIdeas.map((i) => (
        <Link
          key={i.id}
          href={sameStepIn(location, i.id)}
          role="menuitem"
          className="isc-switch__row"
          onClick={() => {
            analytics.track('idea_scope_switch', { step: scopeKeyForPath(location) });
            onDone();
          }}
        >
          <span className="isc-mono isc-mono--sm">{monogram(i.title)}</span>
          <span className="isc-switch__t">{i.title}</span>
        </Link>
      ))}
      <Link href="/ideas" className="isc-switch__all" onClick={onDone}>
        All ideas →
      </Link>
    </div>
  );
}

/* ---------- desktop rail ---------- */

/**
 * The idea-scoped middle of the left rail: the way out, the idea card (opens
 * the switcher), a progress hairline, and every step grouped by phase.
 */
export function IdeaRail({ ideaId, location }: { ideaId: string; location: string }) {
  const ws = useIdeaWorkspace(ideaId);
  const here = scopeKeyForPath(location);
  const [switching, setSwitching] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => setSwitching(false), [location]);
  useEffect(() => {
    if (!switching) return;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setSwitching(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSwitching(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [switching]);

  const next = ws ? nextStepFor(ws.surfaces, here) : null;
  const pct = ws ? ws.doneCount / Math.max(1, ws.total) : 0;

  return (
    <div className="isc-rail">
      <div className="isc-scope" ref={boxRef}>
        <Link href="/ideas" className="isc-out" onClick={() => analytics.track('idea_left', { via: 'rail' })}>
          ← All ideas
        </Link>
        <button
          type="button"
          className="isc-card"
          aria-haspopup="menu"
          aria-expanded={switching}
          onClick={() => setSwitching((v) => !v)}
          disabled={!ws}
        >
          <span className="isc-mono">{ws ? monogram(ws.idea.title) : ''}</span>
          <span className="isc-card__tx">
            <b>{ws?.idea.title ?? '\u00a0'}</b>
            <small>{ws ? `${ws.doneCount} of ${ws.total} steps done ▾` : '\u00a0'}</small>
          </span>
        </button>
        <div className="isc-bar" aria-hidden="true">
          <span style={{ transform: `scaleX(${pct})` }} />
        </div>
        {switching && ws && <Switcher ws={ws} location={location} onDone={() => setSwitching(false)} />}
      </div>
      {ws ? (
        <StepList ws={ws} here={here} next={next} variant="rail" onPick={(to) => track(here, to, 'rail')} />
      ) : (
        <StepListSkeleton />
      )}
    </div>
  );
}

/* ---------- phone ---------- */

/** Slim bar under the top bar on normal idea pages: out, where you are, the map. */
export function IdeaBar({ ideaId, location, onOpenMap }: { ideaId: string; location: string; onOpenMap: () => void }) {
  const ws = useIdeaWorkspace(ideaId);
  const here = scopeKeyForPath(location);
  const byKey = new Map((ws?.surfaces ?? []).map((s) => [s.key, s]));
  return (
    <div className="isc-ibar">
      <Link href="/ideas" className="isc-ibar__back" aria-label="All ideas" onClick={() => analytics.track('idea_left', { via: 'bar' })}>
        ‹
      </Link>
      <div className="isc-ibar__who">
        <small>{ws?.idea.title ?? '\u00a0'}</small>
        <b>{SURFACE_LABEL[here]}</b>
      </div>
      <button type="button" className="isc-ibar__map" onClick={onOpenMap} aria-haspopup="dialog" aria-label="This idea's steps">
        <span className="isc-dots" aria-hidden="true">
          {PHASES.map((ph) => (
            <i key={ph.id} className={ph.keys.some((k) => byKey.get(k)?.status === 'done') ? 'on' : ''} />
          ))}
        </span>
        {ws ? `${ws.doneCount}/${ws.total}` : '–'}
      </button>
    </div>
  );
}

/** The phone's Idea map sheet: Next first, then every step, then the way out. */
export function IdeaMapSheet({
  ideaId,
  open,
  onClose,
  onOpenEverything,
}: {
  ideaId: string;
  open: boolean;
  onClose: () => void;
  onOpenEverything: () => void;
}) {
  const [location] = useLocation();
  const ws = useIdeaWorkspace(ideaId);
  const here = scopeKeyForPath(location);
  const next = ws ? nextStepFor(ws.surfaces, here) : null;
  const [switching, setSwitching] = useState(false);
  const dragControls = useDragControls();
  const sheetRef = useRef<HTMLDivElement>(null);
  const reduce = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    setSwitching(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeRef.current();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onDragEnd = (_: unknown, info: PanInfo) => {
    const h = sheetRef.current?.offsetHeight ?? 600;
    if (info.offset.y > h * 0.3 || info.velocity.y > 600) onClose();
  };

  // Root stays mounted and stops catching taps the moment the sheet closes
  // (see MenuSheet in nav/MobileNav.tsx).
  return createPortal(
    <div className="msheet-root" data-open={open ? 'true' : 'false'}>
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="scrim"
            className="msheet__scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
          />
          <motion.div
            key="sheet"
            ref={sheetRef}
            className="msheet isc-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="This idea's steps"
            initial={{ y: '105%' }}
            animate={{ y: 0 }}
            exit={{ y: '105%' }}
            transition={reduce ? { duration: 0 } : { duration: 0.34, ease: [0.32, 0.72, 0, 1] }}
            drag="y"
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.05, bottom: 0.6 }}
            dragSnapToOrigin
            onDragEnd={onDragEnd}
          >
            <div className="msheet__grab-zone" onPointerDown={(e) => dragControls.start(e)}>
              <div className="msheet__grab" aria-hidden="true" />
              <div className="isc-sheet__head">
                <small>{ws ? `Idea · ${ws.doneCount} of ${ws.total} steps` : 'Idea'}</small>
                <button type="button" className="isc-sheet__title" onClick={() => setSwitching((v) => !v)} aria-expanded={switching}>
                  {ws?.idea.title ?? '\u00a0'} <span aria-hidden="true">▾</span>
                </button>
              </div>
            </div>
            <div className="msheet__body isc-sheet__body">
              {switching && ws ? (
                <Switcher ws={ws} location={location} onDone={onClose} />
              ) : ws ? (
                <>
                  {next && (
                    <Link
                      href={next.href}
                      className="isc-nextcard"
                      onClick={() => {
                        analytics.track('next_step_clicked', { step: next.key, source: 'sheet' });
                        onClose();
                      }}
                    >
                      <span>
                        <small>Next for this idea</small>
                        <b>{SURFACE_LABEL[next.key]}</b>
                      </span>
                      <span className="isc-go" aria-hidden="true">→</span>
                    </Link>
                  )}
                  <StepList
                    ws={ws}
                    here={here}
                    next={next}
                    variant="sheet"
                    onPick={(to) => {
                      track(here, to, 'sheet');
                      onClose();
                    }}
                  />
                </>
              ) : (
                <StepListSkeleton />
              )}
            </div>
            <div className="isc-sheet__foot">
              <Link
                href="/ideas"
                onClick={() => {
                  analytics.track('idea_left', { via: 'sheet' });
                  onClose();
                }}
              >
                ← Leave this idea
              </Link>
              <button type="button" onClick={onOpenEverything}>
                Everything in EntreDash
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
    </div>,
    document.body,
  );
}
